import { useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
} from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import { useInventory } from '../api/queries'
import type { ExpiringLot, StockProduct } from '../api/types'
import { daysUntilExpiry, isExpiringSoon, isOutOfStock } from '../components/expiry'
import { InventoryFilterRail } from '../components/InventoryFilterRail'
import type { LotExpiryBucket, StockFilter } from '../components/inventory-filters'

type ViewMode = 'stock' | 'lots'

export function InventoryPage() {
  const { data, isLoading, isFetching, isError, error, refetch } = useInventory(500)
  const [viewMode, setViewMode] = useState<ViewMode>('stock')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [stockFilter, setStockFilter] = useState<StockFilter>('all')
  const [lotBucket, setLotBucket] = useState<LotExpiryBucket>('all')
  const [filterRailCollapsed, setFilterRailCollapsed] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 200)
    return () => clearTimeout(id)
  }, [search])

  const stockRows = useMemo<StockProduct[]>(
    () => data?.stock_report?.products ?? [],
    [data],
  )
  const lotRows = useMemo<ExpiringLot[]>(() => data?.expiring_lots?.lots ?? [], [data])

  const filteredStock = useMemo(() => {
    const needle = debounced.trim().toLowerCase()
    // Null-guards on both fields: the backend returns rows where
    // product_name or product_code are occasionally null (observed in
    // prod 2026-04-24 — crashed search with TypeError reading
    // 'toLowerCase'). Types were declared non-null but the data wasn't.
    return stockRows.filter((r) => {
      if (stockFilter === 'in_stock' && isOutOfStock(r)) return false
      if (stockFilter === 'out_of_stock' && !isOutOfStock(r)) return false
      if (!needle) return true
      return (
        (r.product_name?.toLowerCase().includes(needle) ?? false) ||
        (r.product_code?.toLowerCase().includes(needle) ?? false)
      )
    })
  }, [stockRows, debounced, stockFilter])

  const filteredLots = useMemo(() => {
    const needle = debounced.trim().toLowerCase()
    // Same null-guard pattern as filteredStock — lot rows occasionally
    // arrive with null lot_name or product.
    return lotRows.filter((r) => {
      const days = daysUntilExpiry(r.expiration_date)
      if (lotBucket === 'expired') {
        if (days === null || days >= 0) return false
      } else if (lotBucket === 'within_30') {
        if (days === null || days < 0 || days > 30) return false
      } else if (lotBucket === 'within_60') {
        if (days === null || days < 0 || days > 60) return false
      }
      if (!needle) return true
      return (
        (r.lot_name?.toLowerCase().includes(needle) ?? false) ||
        (r.product?.toLowerCase().includes(needle) ?? false)
      )
    })
  }, [lotRows, debounced, lotBucket])

  const totalStockValue = useMemo(
    () => stockRows.reduce((sum, r) => sum + (r.stock_value ?? 0), 0),
    [stockRows],
  )
  const outOfStockCount = useMemo(
    () => stockRows.filter((r) => isOutOfStock(r)).length,
    [stockRows],
  )
  const expiringSoonCount = useMemo(
    () => lotRows.filter((r) => isExpiringSoon(r.expiration_date, 30)).length,
    [lotRows],
  )

  const stockColumns = useMemo<ColumnDef<StockProduct, unknown>[]>(
    () => [
      {
        id: 'product_name',
        header: 'Product',
        accessorKey: 'product_name',
        cell: (ctx) => (
          <a
            href={odooUrl('product.product', ctx.row.original.product_id)}
            target="_blank"
            rel="noreferrer"
            className="text-link hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {ctx.row.original.product_name}
          </a>
        ),
      },
      { id: 'product_code', header: 'Code', accessorKey: 'product_code' },
      { id: 'uom', header: 'UoM', accessorKey: 'uom' },
      {
        id: 'qty_on_hand',
        header: 'On hand',
        accessorKey: 'qty_on_hand',
        cell: (ctx) => fmtQty(ctx.getValue()),
      },
      {
        id: 'qty_reserved',
        header: 'Reserved',
        accessorKey: 'qty_reserved',
        cell: (ctx) => fmtQty(ctx.getValue()),
      },
      {
        id: 'qty_available',
        header: 'Available',
        accessorKey: 'qty_available',
        cell: (ctx) => {
          const v = ctx.getValue() as number | null | undefined
          if (v == null) return <span className="text-tertiary">—</span>
          return (
            <span className={v <= 0 ? 'font-semibold text-danger-text' : undefined}>
              {v.toLocaleString()}
            </span>
          )
        },
      },
      {
        id: 'qty_incoming',
        header: 'Incoming',
        accessorKey: 'qty_incoming',
        cell: (ctx) => fmtQty(ctx.getValue()),
      },
      {
        id: 'qty_outgoing',
        header: 'Outgoing',
        accessorKey: 'qty_outgoing',
        cell: (ctx) => fmtQty(ctx.getValue()),
      },
      {
        id: 'qty_forecasted',
        header: 'Forecast',
        accessorKey: 'qty_forecasted',
        cell: (ctx) => fmtQty(ctx.getValue()),
      },
      {
        id: 'stock_value',
        header: 'Value',
        accessorKey: 'stock_value',
        cell: (ctx) => {
          const v = ctx.getValue() as number | null | undefined
          if (v == null) return <span className="text-tertiary">—</span>
          return v.toLocaleString(undefined, {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
          })
        },
      },
    ],
    [],
  )

  const lotColumns = useMemo<ColumnDef<ExpiringLot, unknown>[]>(
    () => [
      {
        id: 'lot_name',
        header: 'Lot #',
        accessorKey: 'lot_name',
        cell: (ctx) => {
          const { id, lot_name } = ctx.row.original
          if (!lot_name) return <span className="text-tertiary">—</span>
          return (
            <a
              href={odooUrl('stock.lot', id)}
              target="_blank"
              rel="noreferrer"
              className="text-link hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {lot_name}
            </a>
          )
        },
      },
      {
        id: 'product',
        header: 'Product',
        accessorKey: 'product',
        cell: (ctx) => (
          <a
            href={odooUrl('product.product', ctx.row.original.product_id)}
            target="_blank"
            rel="noreferrer"
            className="text-link hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {ctx.row.original.product}
          </a>
        ),
      },
      {
        id: 'qty_on_hand',
        header: 'Qty on hand',
        accessorKey: 'qty_on_hand',
        cell: (ctx) => fmtQty(ctx.getValue()),
      },
      {
        id: 'expiration_date',
        header: 'Expires',
        accessorKey: 'expiration_date',
        cell: (ctx) => ctx.row.original.expiration_date ?? '—',
      },
      { id: 'use_date', header: 'Use by', accessorKey: 'use_date' },
      {
        id: 'days_left',
        header: 'Days',
        cell: (ctx) => {
          const d = daysUntilExpiry(ctx.row.original.expiration_date)
          if (d === null) return <span className="text-tertiary">—</span>
          if (d < 0) {
            return (
              <Chip size="xs" variant="danger" dot>
                EXPIRED
              </Chip>
            )
          }
          if (d <= 30) {
            return (
              <Chip size="xs" variant="warning" dot>
                {d}d
              </Chip>
            )
          }
          return <span className="text-secondary">{d}d</span>
        },
      },
    ],
    [],
  )

  // `/` focuses search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return
      if (e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
      }
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title="Inventory"
        description="Stock levels and expiring lots across all warehouses."
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard
            variant="inline"
            label="Stock value"
            value={totalStockValue.toLocaleString(undefined, {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            })}
          />
          <MetricCard variant="inline" label="Products" value={stockRows.length.toLocaleString()} />
          <MetricCard
            variant="inline"
            label="Out of stock"
            value={outOfStockCount.toLocaleString()}
            valueTone={outOfStockCount > 0 ? 'warning' : 'default'}
          />
          <MetricCard
            variant="inline"
            label="Expiring < 30d"
            value={expiringSoonCount.toLocaleString()}
            valueTone={expiringSoonCount > 0 ? 'warning' : 'default'}
          />
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1">
        <InventoryFilterRail
          viewMode={viewMode}
          stockFilter={stockFilter}
          onStockFilterChange={setStockFilter}
          lotBucket={lotBucket}
          onLotBucketChange={setLotBucket}
          collapsed={filterRailCollapsed}
          onToggleCollapsed={() => setFilterRailCollapsed((v) => !v)}
        />

        <div className="flex min-w-0 flex-1 flex-col">
        <Toolbar>
          <SearchInput
            ref={searchRef}
            containerClassName="flex-1 max-w-none"
            placeholder={viewMode === 'stock' ? 'Search product…' : 'Search lot or product…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
        </Toolbar>
        <Toolbar>
          <ToolbarLeft>
            <ViewToggle
              aria-label="View mode"
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'stock', label: 'Stock levels' },
                { value: 'lots', label: 'Expiring lots' },
              ]}
            />
          </ToolbarLeft>
          <ToolbarRight>
            <ToolbarCount>
              {viewMode === 'stock'
                ? `${filteredStock.length.toLocaleString()} of ${stockRows.length.toLocaleString()}`
                : `${filteredLots.length.toLocaleString()} of ${lotRows.length.toLocaleString()}`}
            </ToolbarCount>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={isFetching && !isLoading}
              onClick={() => refetch()}
            >
              <RefreshCw size={14} />
            </Button>
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 min-h-0 overflow-hidden bg-canvas p-3">
          {viewMode === 'stock' ? (
            <DataTable
              data={filteredStock}
              columns={stockColumns}
              loading={isLoading}
              error={isError ? error : undefined}
              onRetry={() => void refetch()}
              errorSubject="stock levels"
              criticalRowPredicate={isOutOfStock}
              getRowId={(r) => String(r.product_id)}
              moduleId="inventory-stock"
              maxHeight="100%"
              columnWidths={{
                product_code: 130,
                uom: 60,
                qty_on_hand: 90,
                qty_reserved: 90,
                qty_available: 90,
                qty_incoming: 90,
                qty_outgoing: 90,
                qty_forecasted: 90,
                stock_value: 120,
              }}
              minTableWidth={1100}
              emptyState={
                <EmptyState
                  variant="no-match"
                  title="No products match"
                  description="Clear the search to see all stock."
                />
              }
            />
          ) : (
            <DataTable
              data={filteredLots}
              columns={lotColumns}
              loading={isLoading}
              error={isError ? error : undefined}
              onRetry={() => void refetch()}
              errorSubject="lots"
              criticalRowPredicate={(row) =>
                isExpiringSoon(row.expiration_date, 30)
              }
              getRowId={(r) => String(r.id)}
              moduleId="inventory-lots"
              maxHeight="100%"
              columnWidths={{
                lot_name: 130,
                qty_on_hand: 90,
                expiration_date: 110,
                use_date: 110,
                days_left: 80,
              }}
              emptyState={
                // Sprint 09 Item 8 / D-8: surface the actual filter
                // shape so an empty list isn't a mystery. The lots
                // view loads `/api/reports/inventory` which calls
                // handle_get_lot_details with `expiring_within_days=90`
                // + an implicit `product_qty > 0` filter. When SPI's
                // expiring stock is depleted OR no lots have expiry
                // dates set in Odoo, the list is legitimately empty.
                debounced.trim() || lotBucket !== 'all' ? (
                  <EmptyState
                    variant="no-match"
                    title="No lots match"
                    description="Clear the search and reset the expiry window to see all tracked lots."
                  />
                ) : (
                  <EmptyState
                    variant="no-data"
                    title="No expiring lots"
                    description="The lots view shows non-empty stock lots that expire within the next 90 days. If you're expecting lots here, verify that expiration dates are set on the underlying stock.lot records in Odoo."
                  />
                )
              }
            />
          )}
        </div>
        </div>
      </div>
    </div>
  )
}

/** Null-safe numeric cell renderer. Returns an em dash for null /
 *  undefined so a missing backend field renders as "—" instead of
 *  crashing the cell with `undefined.toLocaleString()`. The Inventory
 *  backend returns rows where these quantity fields are occasionally
 *  null (observed in prod 2026-04-24). */
function fmtQty(v: unknown): React.ReactNode {
  if (v == null) return <span className="text-tertiary">—</span>
  if (typeof v !== 'number') return String(v)
  return v.toLocaleString()
}
